import {
  categoricalOnlySelection,
  expandDesignWeights,
  recommendedZoneCount,
  CATEGORICAL_FULL_CLASSES,
} from "./zoneEdit";
import { expandCategoricalFeatures, hashSeed } from "./samplingDesign";
import { designZones, type ZoneGeom } from "./zoneDesign";
import { mergeToCount, splitToCount } from "./zoneSplit";

// The relative-zone recompute, without the editor session around it. The store still decides
// when to run this and where the labels go.

export interface RelativeZoneDesign {
  computed: number[];
  ev: number | null;
  suggestedK: number | null;
  /** False when the user has already chosen k, so the caller must leave k alone. */
  kSet: boolean;
  k: number | null;
}

interface Prepared {
  fieldId: string;
  features: (number | null)[][];
  feats: (number | null)[][];
  weights: number[];
  geoms: { type: string; coordinates: unknown }[];
  geom: ZoneGeom;
  rec: number | null;
  kForDesign: number | null;
  rawDistinct: number;
  catOnly: boolean;
  kTouched: boolean;
  k: number | null;
  boundaryAcres: number | undefined;
  minZoneAcres: number;
}

function finish(
  prepared: Prepared,
  computed: number[],
  ev: number | null,
  suggestedK: number | null,
): RelativeZoneDesign {
  return {
    computed,
    ev,
    suggestedK,
    kSet: !prepared.kTouched,
    k: prepared.kTouched ? prepared.k : suggestedK,
  };
}

function prepare(input: {
  fieldId: string;
  checked: string[];
  features: (number | null)[][];
  geoms: { type: string; coordinates: unknown }[];
  geom: ZoneGeom;
  weightOf: (key: string) => number;
  kTouched: boolean;
  k: number | null;
  boundaryAcres: number | undefined;
  minZoneAcres: number;
}): Prepared {
  const catOnly = categoricalOnlySelection(input.checked);
  const maxClasses = catOnly ? CATEGORICAL_FULL_CLASSES : 5;
  const rec = recommendedZoneCount(input.checked, input.features);
  const rawDistinct = catOnly
    ? new Set(
        (input.features[0] ?? []).filter((v) => Number.isFinite(v as number)),
      ).size
    : 0;
  return {
    fieldId: input.fieldId,
    features: input.features,
    feats: expandCategoricalFeatures(input.checked, input.features, maxClasses),
    weights: expandDesignWeights(
      input.checked,
      input.features,
      input.weightOf,
      maxClasses,
    ),
    geoms: input.geoms,
    geom: input.geom,
    rec,
    kForDesign: input.kTouched ? input.k : rec,
    rawDistinct,
    catOnly,
    kTouched: input.kTouched,
    k: input.k,
    boundaryAcres: input.boundaryAcres,
    minZoneAcres: input.minZoneAcres,
  };
}

// Classes are identities. k-means away from the class count only splits them as seed noise,
// so a categorical-only selection never takes the mixed probe-and-exact path.
function categoricalDesign(prepared: Prepared): RelativeZoneDesign | null {
  const { kForDesign, rawDistinct, catOnly } = prepared;
  if (
    !catOnly ||
    kForDesign == null ||
    rawDistinct < 1 ||
    rawDistinct > CATEGORICAL_FULL_CLASSES
  ) {
    return null;
  }
  let base: number[];
  let baseEv: number | null = null;
  if (rawDistinct >= 2) {
    const bres = designZones({
      geoms: prepared.geoms,
      features: prepared.feats,
      weights: prepared.weights,
      k: rawDistinct,
      protectK: true,
      seed: hashSeed(prepared.fieldId),
      minZoneAcres: prepared.minZoneAcres,
      boundaryAcres: prepared.boundaryAcres,
    });
    base = bres.labels;
    baseEv = bres.ev;
  } else {
    base = (prepared.features[0] ?? []).map((v) => (v == null ? -1 : 1));
  }
  const labels =
    kForDesign > rawDistinct
      ? splitToCount(
          base,
          prepared.geom.centroids,
          prepared.geom.adj,
          kForDesign,
        )
      : kForDesign < rawDistinct
        ? mergeToCount(base, prepared.geom.adj, kForDesign)
        : base;
  return finish(
    prepared,
    labels,
    kForDesign >= rawDistinct ? baseEv : null,
    prepared.rec,
  );
}

function elbowDesign(prepared: Prepared): RelativeZoneDesign {
  const res = designZones({
    geoms: prepared.geoms,
    features: prepared.feats,
    weights: prepared.weights,
    k: prepared.kForDesign ?? undefined,
    seed: hashSeed(prepared.fieldId),
    minZoneAcres: prepared.minZoneAcres,
    boundaryAcres: prepared.boundaryAcres,
  });
  // The recommendation is the count the auto design delivers, not the elbow's internal k.
  return finish(
    prepared,
    res.labels,
    res.ev,
    prepared.rec ?? res.autoDeliveredK ?? res.autoK,
  );
}

export function designRelativeZones(input: {
  fieldId: string;
  checked: string[];
  features: (number | null)[][];
  geoms: { type: string; coordinates: unknown }[];
  geom: ZoneGeom;
  weightOf: (key: string) => number;
  kTouched: boolean;
  k: number | null;
  boundaryAcres: number | undefined;
  minZoneAcres: number;
}): RelativeZoneDesign {
  const prepared = prepare(input);
  return categoricalDesign(prepared) ?? elbowDesign(prepared);
}
